/**
 * Preferences client for managing user email subscription preferences.
 * Handles getting and updating user preferences for game reminders and marketing emails.
 */

import { apiClient } from './apiClient';
import type { EmailPreferences } from '@mental-mint/shared-types';

export interface GetPreferencesResponse {
  success: boolean;
  preferences?: EmailPreferences;
  error?: string;
}

export interface UpdatePreferencesResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Get email subscription preferences for a user.
 * @param playerId - The unique player ID
 * @returns The user's email preferences
 */
export async function getPreferences(playerId: string): Promise<EmailPreferences> {
  const response = await apiClient<GetPreferencesResponse>(
    `/preferences/get?playerId=${encodeURIComponent(playerId)}`,
    { method: 'GET' }
  );

  if (!response.success || !response.preferences) {
    throw new Error(response.error || 'Failed to fetch preferences');
  }

  return response.preferences;
}

/**
 * Update email subscription preferences for a user.
 * @param playerId - The unique player ID
 * @param preferences - Partial preferences to update
 */
export async function updatePreferences(
  playerId: string,
  preferences: Partial<EmailPreferences>
): Promise<void> {
  const response = await apiClient<UpdatePreferencesResponse>('/preferences/update', {
    method: 'POST',
    body: JSON.stringify({
      playerId,
      ...preferences,
    }),
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to update preferences');
  }
}
